import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { accessibleDivisionNames } from "../lib/access";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, X, ArrowLeft, Radio, Users, UserCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { nowZonedWall, zonedWallToUtcIso, utcIsoToZonedWall, tzBanner } from "../lib/timezone";

const TARGETS = [
  { value: "specific_shift", label: "Specific Shift", icon: Radio, hint: "One shift in a division" },
  { value: "all_shifts", label: "All Shifts in Division", icon: Users, hint: "Everyone in a division" },
  { value: "selected_people", label: "Selected People", icon: UserCheck, hint: "Pick individual people" },
];

export default function CreateHandoff() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState("");
  const tz = user?.org_timezone;
  const nowLocal = nowZonedWall(tz);

  const [form, setForm] = useState({
    shift_name: "",
    title: "",
    date_time: nowLocal,
    summary: "",
    incidents: "",
    equipment_issues: "",
    priority: "normal",
    next_shift: "",
    division: "",
  });
  const [recipientTarget, setRecipientTarget] = useState("specific_shift");
  const [people, setPeople] = useState([]);           // recipient options {id,name,primary_division,shift_name}
  const [selectedPeople, setSelectedPeople] = useState([]);  // ids
  const [peopleSearch, setPeopleSearch] = useState("");
  const [incoming, setIncoming] = useState(null);

  useEffect(() => {
    if (recipientTarget === "specific_shift" && form.division && form.next_shift) {
      api.get(`/schedule/incoming?division=${encodeURIComponent(form.division)}&shift=${encodeURIComponent(form.next_shift)}`)
        .then((r) => setIncoming(r.data)).catch(() => setIncoming(null));
    } else {
      setIncoming(null);
    }
  }, [form.division, form.next_shift, recipientTarget]);

  useEffect(() => {
    api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
    api.get("/handoffs/recipient-options").then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/handoffs/${id}`).then((r) => {
      const h = r.data;
      setForm({
        shift_name: h.shift_name || "",
        title: h.title || "",
        date_time: utcIsoToZonedWall(h.date_time, tz),
        summary: h.summary || "",
        incidents: h.incidents || "",
        equipment_issues: h.equipment_issues || "",
        priority: h.priority || "normal",
        next_shift: h.next_shift || "",
        division: h.division || "",
      });
      setRecipientTarget(h.recipient_target || (h.next_shift ? "specific_shift" : "all_shifts"));
      setSelectedPeople(h.recipient_user_ids || []);
      setTasks((h.tasks || []).map((t) => t.title || t).filter(Boolean));
    }).catch((err) => { toast.error(formatError(err.response?.data?.detail)); navigate("/dashboard"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, tz]);

  const activeDivisions = divisions.filter((d) => d.active !== false);
  const allowedNames = accessibleDivisionNames(user, activeDivisions);
  const myDivisions = activeDivisions.filter((d) => allowedNames.includes(d.name));

  useEffect(() => {
    if (!form.division && myDivisions.length === 1) {
      setForm((p) => ({ ...p, division: myDivisions[0].name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const creatorShift = isEdit ? form.shift_name : (user?.shift_name || "");
  const equipmentOnly =
    (form.equipment_issues || "").trim().length > 0 &&
    !(form.summary || "").trim() &&
    !(form.incidents || "").trim() &&
    tasks.length === 0;

  const togglePerson = (id) =>
    setSelectedPeople((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const addTask = () => {
    if (taskInput.trim()) {
      setTasks([...tasks, taskInput.trim()]);
      setTaskInput("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.division) { toast.error("Please select a division"); return; }
    if (!form.title.trim() && !equipmentOnly) { toast.error("Please add a handoff title"); return; }
    const hasContent = [form.summary, form.incidents, form.equipment_issues]
      .some((v) => (v || "").trim().length > 0) || tasks.length > 0;
    if (!hasContent) { toast.error("Please enter at least one handoff detail before submitting."); return; }
    if (recipientTarget === "specific_shift" && !form.next_shift) {
      toast.error("Please choose the specific shift to send to"); return;
    }
    if (recipientTarget === "selected_people" && selectedPeople.length === 0) {
      toast.error("Please select at least one recipient"); return;
    }
    setBusy(true);
    try {
      const payload = {
        date_time: zonedWallToUtcIso(form.date_time, tz),
        title: form.title.trim(),
        summary: form.summary,
        incidents: form.incidents,
        equipment_issues: form.equipment_issues,
        priority: form.priority,
        division: form.division,
        recipient_target: recipientTarget,
        next_shift: recipientTarget === "specific_shift" ? form.next_shift : "",
        recipient_user_ids: recipientTarget === "selected_people" ? selectedPeople : [],
        tasks,
      };
      const { data } = isEdit
        ? await api.put(`/handoffs/${id}`, payload)
        : await api.post("/handoffs", payload);
      toast.success(isEdit ? "Handoff updated" : "Handoff published");
      navigate(`/handoffs/${data.id}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

  return (
    <div className="mx-auto max-w-3xl animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">{isEdit ? "Edit Handoff" : "Create Handoff"}</h1>
      <p className="mt-1 text-sm text-slate-500">{isEdit ? "Update the details, priority, or recipients — the division stays the same." : "Pass the essentials to the next shift."}</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <Card className="space-y-5 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <Label htmlFor="title" className={labelCls}>Handoff title {equipmentOnly ? <span className="text-slate-400">(optional for equipment-only reports)</span> : <span className="text-red-500">*</span>}</Label>
            <Input
              id="title"
              data-testid="handoff-title-input"
              value={form.title}
              onChange={set("title")}
              maxLength={80}
              required={!equipmentOnly}
              className="mt-1.5 text-base font-semibold"
              placeholder="e.g. Road Closure Update"
            />
            <p className="mt-1 flex items-center justify-between text-xs text-slate-400">
              <span>A short subject line so teammates can scan handoffs at a glance.</span>
              <span data-testid="handoff-title-count">{form.title.length}/80</span>
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className={labelCls}>Your shift {isEdit ? "(creator shift, locked)" : "(auto)"}</Label>
              <div data-testid="handoff-creator-shift" className="mt-1.5 flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
                {creatorShift || "Unassigned"}
              </div>
              <p className="mt-1 text-xs text-slate-400">Automatically your assigned shift — this is who the handoff is <span className="font-medium">from</span>, not who it's sent to.</p>
            </div>
            <div>
              <Label htmlFor="dt" className={labelCls}>Date & time</Label>
              <Input id="dt" type="datetime-local" data-testid="handoff-datetime-input" value={form.date_time} onChange={set("date_time")} required className="mt-1.5" />
              <p className="mt-1 text-xs text-slate-400" data-testid="handoff-tz-note">{tzBanner(tz)}</p>
            </div>
          </div>

          <div>
            <Label htmlFor="summary" className={labelCls}>Shift summary (optional)</Label>
            <Textarea id="summary" data-testid="handoff-summary-input" value={form.summary} onChange={set("summary")} rows={3} className="mt-1.5" placeholder="What happened during this shift?" />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="incidents" className={labelCls}>Notable incidents</Label>
              <Textarea id="incidents" data-testid="handoff-incidents-input" value={form.incidents} onChange={set("incidents")} rows={3} className="mt-1.5" placeholder="Any incidents to report" />
            </div>
            <div>
              <Label htmlFor="equip" className={labelCls}>Equipment issues</Label>
              <Textarea id="equip" data-testid="handoff-equipment-input" value={form.equipment_issues} onChange={set("equipment_issues")} rows={3} className="mt-1.5" placeholder="Broken cameras, vehicles, etc." />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className={labelCls}>Priority level</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="handoff-priority-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Division{isEdit && " (locked)"}</Label>
              <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })} disabled={isEdit}>
                <SelectTrigger data-testid="handoff-division-select" className="mt-1.5">
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  {myDivisions.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {myDivisions.length === 0 && (
                <p className="mt-1.5 text-xs text-red-500">You have no authorized divisions. Ask an admin for access.</p>
              )}
            </div>
          </div>

          {/* ===== SEND TO / INTENDED RECIPIENTS ===== */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="handoff-send-to">
            <Label className={labelCls}>Send to</Label>
            <p className="mt-1 text-xs text-slate-500">Choose exactly who should see and be notified about this handoff.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {TARGETS.map((t) => {
                const Icon = t.icon;
                const active = recipientTarget === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    data-testid={`target-${t.value}`}
                    onClick={() => setRecipientTarget(t.value)}
                    className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${active ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <span className={`flex items-center gap-1.5 text-sm font-semibold ${active ? "text-blue-700" : "text-slate-700"}`}>
                      <Icon className="h-4 w-4" /> {t.label}
                    </span>
                    <span className="text-[11px] text-slate-500">{t.hint}</span>
                  </button>
                );
              })}
            </div>

            {recipientTarget === "specific_shift" && (
              <div className="mt-4" data-testid="target-specific-shift-fields">
                <Label className={labelCls}>Shift</Label>
                <Select value={form.next_shift} onValueChange={(v) => setForm({ ...form, next_shift: v })}>
                  <SelectTrigger data-testid="handoff-nextshift-select" className="mt-1.5 sm:max-w-xs">
                    <SelectValue placeholder="Select the shift to send to" />
                  </SelectTrigger>
                  <SelectContent>
                    {shifts.map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-slate-500">
                  Only people on <span className="font-semibold">{form.next_shift || "the selected shift"}</span> in{" "}
                  <span className="font-semibold">{form.division || "the division"}</span> will be notified.
                </p>
              </div>
            )}

            {recipientTarget === "all_shifts" && (
              <div className="mt-4 rounded-md border border-blue-100 bg-blue-50/70 p-3" data-testid="target-all-shifts-note">
                <p className="text-sm text-blue-800">
                  Everyone in <span className="font-semibold">{form.division || "the selected division"}</span>, across{" "}
                  <span className="font-semibold">every shift</span>, will see and be notified about this handoff.
                </p>
              </div>
            )}

            {recipientTarget === "selected_people" && (
              <div className="mt-4" data-testid="target-selected-people-fields">
                <Label className={labelCls}>Recipients</Label>
                {selectedPeople.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="selected-people-chips">
                    {selectedPeople.map((id) => {
                      const p = people.find((x) => x.id === id);
                      return (
                        <span key={id} data-testid={`selected-person-${id}`} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                          {p?.name || "Member"}
                          <button type="button" onClick={() => togglePerson(id)} className="hover:text-blue-200"><X className="h-3 w-3" /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="relative mt-2">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="people-search"
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    placeholder="Search people by name…"
                    className="pl-8"
                  />
                </div>
                <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-1" data-testid="people-list">
                  {people
                    .filter((p) => (p.name || "").toLowerCase().includes(peopleSearch.toLowerCase()))
                    .map((p) => (
                      <label key={p.id} data-testid={`person-option-${p.id}`} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        <Checkbox checked={selectedPeople.includes(p.id)} onCheckedChange={() => togglePerson(p.id)} />
                        <span className="flex-1">{p.name}</span>
                        <span className="text-[11px] text-slate-400">{p.primary_division || "—"}{p.shift_name ? ` · ${p.shift_name}` : ""}</span>
                      </label>
                    ))}
                  {people.length === 0 && <p className="px-2 py-3 text-xs text-slate-400">No eligible people in your divisions.</p>}
                  {people.length > 0 && people.filter((p) => (p.name || "").toLowerCase().includes(peopleSearch.toLowerCase())).length === 0 && (
                    <p className="px-2 py-3 text-xs text-slate-400">No matches for “{peopleSearch}”.</p>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-slate-500">Only the people you select will see and be notified about this handoff.</p>
              </div>
            )}
          </div>

          {recipientTarget === "specific_shift" && incoming && (incoming.employees.length > 0 || incoming.supervisor) && (
            <div data-testid="incoming-suggestions" className="rounded-md border border-blue-100 bg-blue-50/50 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Suggested incoming recipients · {form.next_shift}</p>
              {incoming.supervisor && (
                <p className="mt-1 text-sm text-slate-700">Incoming supervisor: <span className="font-semibold">{incoming.supervisor.name}</span></p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {incoming.employees.map((e) => (
                  <span key={e.id} data-testid={`incoming-emp-${e.id}`} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">{e.name}</span>
                ))}
                {incoming.employees.length === 0 && <span className="text-xs text-slate-500">No scheduled personnel found for this shift.</span>}
              </div>
              <p className="mt-2 text-[11px] text-slate-400">These authorized users will be notified when you publish.</p>
            </div>
          )}
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <Label className={labelCls}>Open tasks</Label>
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="handoff-task-input"
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
              placeholder="Add a task for the next shift"
            />
            <Button type="button" data-testid="handoff-add-task" onClick={addTask} variant="outline" className="gap-1 shrink-0">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
          <div className="mt-3 space-y-2" data-testid="handoff-task-list">
            {tasks.map((t, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">{t}</span>
                <button type="button" onClick={() => setTasks(tasks.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {tasks.length === 0 && <p className="text-sm text-slate-400">No tasks added yet.</p>}
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>Cancel</Button>
          <Button type="submit" data-testid="handoff-submit-button" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white">
            {busy ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save Changes" : "Publish Handoff")}
          </Button>
        </div>
      </form>
    </div>
  );
}
